/**
 * Two-factor authentication — wire types shared by the browser client, the
 * auth store and the Next.js proxy routes.
 *
 * Every BE auth entry (`CompleteUserSession`) answers a discriminated union:
 * the usual session body, or `{ two_factor_required: true, challenge_token }`
 * when the account has TOTP enabled. Success bodies carry
 * `two_factor_required: false`, so the discriminator is the literal `true`,
 * never the field's presence. Contract: `tabootv_bo/documentation/api-user-2fa.md`.
 */

export interface TwoFactorRequiredResponse {
  message?: string;
  two_factor_required: true;
  /**
   * Sanctum token with the single `2fa:pending` ability. Only `/api/2fa/verify`
   * accepts it; every other endpoint answers 403. Lives in React state for the
   * duration of the challenge — never a cookie, never storage, never a log line.
   */
  challenge_token: string;
}

export function isTwoFactorRequired(response: unknown): response is TwoFactorRequiredResponse {
  if (typeof response !== 'object' || response === null) return false;
  const body = response as Record<string, unknown>;
  return body.two_factor_required === true && typeof body.challenge_token === 'string';
}

/** What an auth-store login call resolves to; callers branch before navigating. */
export type LoginOutcome =
  { twoFactorRequired: false } | { twoFactorRequired: true; challengeToken: string };

/** Store-level input for `completeTwoFactor`. Exactly one of `code` / `recoveryCode`. */
export interface TwoFactorCompleteInput {
  challengeToken: string;
  code?: string;
  recoveryCode?: string;
  rememberMe?: boolean;
}

/** Browser → `/api/2fa/verify` proxy body. Exactly one of `code` / `recovery_code`. */
export interface TwoFactorVerifyPayload {
  challenge_token: string;
  code?: string;
  recovery_code?: string;
  /** The cookie lifetime is decided at verify time now, not at `/api/login`. */
  remember_me?: boolean;
}
